import { getBinaryDocument } from '@ctw/shared/api/fhir/binaries';
import { usePatientDocumentsList } from '@ctw/shared/api/fhir/document';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import {
  useMainContentDetailPanel,
  useMainContentHeader,
} from '@ctw/shared/components/layout/main-content-with-dockable-drawer';
import type { LegacyRowActionsConfigProp } from '@ctw/shared/components/tables/legacy-table';
import { DocumentViewer } from '@ctw/shared/content/document-viewer/document-viewer';
import { EmptyPatientTable } from '@ctw/shared/content/empty-patients-table';
import { ResourceTable } from '@ctw/shared/content/resource/resource-table';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { useFilteredSortedData } from '@ctw/shared/hooks/use-filtered-sorted-data';
import { pathWithParameters } from '@ctw/shared/utils/url';
import type { Binary } from 'fhir/r4';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ResourceTableActions } from '../resource/resource-table-actions';
import { getDateRangeView } from '../resource/view-date-range';
import { useDocumentColumns } from './helpers/columns';
import { documentFilterPredicates, documentsFilter } from './helpers/filters';
import { defaultDocumentSort, documentSortOptions } from './helpers/sorts';

export const PatientDocuments = withErrorBoundary({
  boundaryName: 'PatientDocuments',
  Component: () => {
    const [viewingDocument, internalSetViewingDocument] = useState<DocumentModel | null>(null);
    const { navigate } = useZuiContext();
    const hasShowSuspectedDuplicateDocumentsFeature = useFeatureFlag(
      'ctw-show-suspected-duplicate-documents',
    );

    const { patient } = usePatientContext();
    const patientDocumentQuery = usePatientDocumentsList({
      excludeSuspectedDuplicates: !hasShowSuspectedDuplicateDocumentsFeature,
    });
    const validFilters = useMemo(
      () => documentsFilter(patientDocumentQuery.data, true),
      [patientDocumentQuery.data],
    );
    const rowActions = useRowActions();
    const { viewOptions, allTime } = useMemo(
      () => getDateRangeView<DocumentModel>('dateCreated'),
      [],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
    const setViewingDocument = useCallback((documentReference: DocumentModel | null) => {
      navigate(
        pathWithParameters(
          window.location.pathname,
          {
            documentId: documentReference?.id ?? null,
          },
          ['builderId'],
        ),
      );

      const timeout = setTimeout(() => {
        if (documentReference === null) {
          internalSetViewingDocument(null);
        } else {
          internalSetViewingDocument(documentReference);
        }
      }, 150);

      return () => clearTimeout(timeout);
    }, []);

    useEffect(() => {
      const timeout = setTimeout(() => {
        const urlDocumentId = new URLSearchParams(window.location.search).get('documentId');
        if (urlDocumentId && !viewingDocument && patientDocumentQuery.data) {
          internalSetViewingDocument(
            patientDocumentQuery.data.find(
              (documentReference) => documentReference.id === urlDocumentId,
            ) ?? null,
          );
        }
      }, 150);

      return () => clearTimeout(timeout);
    }, [viewingDocument, patientDocumentQuery]);

    useMainContentDetailPanel(
      'DocumentPanel',
      viewingDocument?.binaryId
        ? () => (
            <DocumentViewer
              documentReference={viewingDocument}
              onClose={() => {
                setViewingDocument(null);
              }}
              controls={{
                search: true,
                close: true,
                download: true,
                print: true,
                openInNewTab: true,
                deepLinkSections: true,
              }}
            />
          )
        : null,
      [setViewingDocument, viewingDocument?.id],
    );

    const { data, filters, setFilters, setSort, setViewOption, defaultSort, defaultView } =
      useFilteredSortedData({
        cacheKey: 'patient-documents',
        defaultSort: defaultDocumentSort,
        viewOptions,
        defaultView: allTime.display,
        records: patientDocumentQuery.data,
        filterPredicates: documentFilterPredicates,
      });
    const isEmptyQuery = patientDocumentQuery.data.length === 0;
    const hasZeroFilteredRecords = !isEmptyQuery && data.length === 0;

    useMainContentHeader(
      'PatientDocumentContentHeader',
      () => (
        <ResourceTableActions
          filterOptions={{
            onChange: setFilters,
            filters: validFilters,
            selected: filters,
          }}
          sortOptions={{
            defaultSort,
            options: documentSortOptions,
            onChange: setSort,
          }}
          viewOptions={{
            onChange: setViewOption,
            options: viewOptions,
            defaultView,
          }}
        />
      ),
      [validFilters, filters, defaultSort, documentSortOptions, viewOptions, defaultView],
    );

    const columns = useDocumentColumns();

    return (
      <ResourceTable
        title="Documents"
        titleVisuallyHidden={true}
        stackedBreakpoint={'sm'}
        isLoading={patientDocumentQuery.isLoading}
        data={data}
        emptyMessage={
          <EmptyPatientTable
            hasZeroFilteredRecords={hasZeroFilteredRecords}
            resourceName="documents"
          />
        }
        columns={columns}
        onCmdOrCtrlClick={(document) => {
          if (!document.binaryId) {
            return;
          }

          const url = `/patient/${patient.UPID}/document/${document.id}`;
          window.open(url, '_blank');
        }}
        onRowClick={({ record }) => {
          if (!record.binaryId) {
            return;
          }

          setViewingDocument(record);
        }}
        rowActions={rowActions}
        enableDismissAndReadActions={true}
      />
    );
  },
});

function useRowActions(onAddToRecord?: (document: DocumentModel, binary: Binary) => void) {
  const { fetchFromFqs } = useCTW();

  return (record: DocumentModel): LegacyRowActionsConfigProp<DocumentModel> => {
    const { binaryId } = record;
    const actions: LegacyRowActionsConfigProp<DocumentModel> = [];

    if (binaryId && onAddToRecord) {
      actions.push({
        className:
          'rounded-md border border-transparent px-4 py-2 text-sm font-medium shadow-sm btn bg-primary-main text-white hover:bg-primary-text ml-1 capitalize',
        testId: 'add-to-record',
        text: 'Add to Record',
        onClick: async () => {
          const { binary } = await getBinaryDocument(fetchFromFqs, binaryId);
          onAddToRecord(record, binary);
        },
      });
    }

    return actions;
  };
}
